// Модель <head> страницы и её сериализация в HTML. Без DOM, этот код
// выполняется в Node при пререндере. Применение к живому документу, applyHead.ts.

import { pageJsonLd, type SeoPage } from './pages';
import { DEFAULT_OG_IMAGE, OG_IMAGE_SIZE, SITE_NAME, SITE_URL } from './site';
import type { JsonLd } from './jsonld';

export interface MetaTag {
  name?: string;
  property?: string;
  content: string;
}

export interface LinkTag {
  rel: string;
  href: string;
}

export interface HeadModel {
  title: string;
  metas: MetaTag[];
  links: LinkTag[];
  jsonLd: JsonLd[];
}

/** Атрибут, по которому RouteSeo отличает свои теги от статических из index.html. */
export const SEO_ATTR = 'data-seo';

export function absoluteUrl(path: string): string {
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

export function buildHead(page: SeoPage): HeadModel {
  const canonical = absoluteUrl(page.path);
  const image = `${SITE_URL}${page.ogImage ?? DEFAULT_OG_IMAGE}`;
  const indexable = !page.noindex;

  const metas: MetaTag[] = [
    { name: 'description', content: page.description },
    {
      name: 'robots',
      content: indexable ? 'index, follow, max-image-preview:large' : 'noindex, nofollow',
    },
  ];
  if (page.keywords) metas.push({ name: 'keywords', content: page.keywords });

  metas.push(
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: page.title },
    { property: 'og:description', content: page.description },
    { property: 'og:url', content: canonical },
    { property: 'og:image', content: image },
    { property: 'og:image:type', content: 'image/png' },
    { property: 'og:image:width', content: String(OG_IMAGE_SIZE.width) },
    { property: 'og:image:height', content: String(OG_IMAGE_SIZE.height) },
    { property: 'og:image:alt', content: page.title },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: page.title },
    { name: 'twitter:description', content: page.description },
    { name: 'twitter:image', content: image },
  );

  // Canonical у noindex-страниц не нужен: он приглашает поисковик считать адрес основным.
  const links: LinkTag[] = indexable ? [{ rel: 'canonical', href: canonical }] : [];

  return {
    title: page.title,
    metas,
    links,
    jsonLd: indexable ? pageJsonLd(page) : [],
  };
}

// ── Сериализация в HTML ──

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** `</script>` внутри JSON-LD не должен закрыть тег, экранируем `<` и `>` в unicode. */
export function serializeJsonLd(node: JsonLd): string {
  const doc = '@context' in node ? node : { '@context': 'https://schema.org', ...node };
  return JSON.stringify(doc).replace(/</g, '\\u003c').replace(/>/g, '\\u003e');
}

export function renderHead(model: HeadModel): string {
  const lines: string[] = [`<title>${escapeHtml(model.title)}</title>`];
  for (const m of model.metas) {
    const key = m.name ? `name="${escapeHtml(m.name)}"` : `property="${escapeHtml(m.property ?? '')}"`;
    lines.push(`<meta ${key} content="${escapeHtml(m.content)}" ${SEO_ATTR} />`);
  }
  for (const l of model.links) {
    lines.push(`<link rel="${escapeHtml(l.rel)}" href="${escapeHtml(l.href)}" ${SEO_ATTR} />`);
  }
  for (const node of model.jsonLd) {
    lines.push(`<script type="application/ld+json" ${SEO_ATTR}>${serializeJsonLd(node)}</script>`);
  }
  return lines.join('\n    ');
}
